import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { dashboardService } from '../services/dashboardService';
import { formatCurrency, formatNumber } from '../utils/formatters';
import { 
  Users, 
  GraduationCap, 
  FileText, 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  Clock,
  AlertCircle,
  CheckCircle2,
  Loader,
  MoreVertical,
  ArrowUpRight,
  UserCheck,
  BookOpen,
  BarChart3,
  Zap,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';

const StatsCard = ({ title, value, subtitle, icon: Icon, trend, loading, color = 'primary' }) => {
  const colorClasses = {
    primary: 'from-blue-500 to-blue-600',
    green: 'from-emerald-500 to-emerald-600',
    purple: 'from-purple-500 to-purple-600',
    orange: 'from-orange-500 to-orange-600'
  };

  const iconBgClasses = {
    primary: 'bg-blue-100 text-blue-600',
    green: 'bg-emerald-100 text-emerald-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600'
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-all duration-300 group">
      <div className="flex justify-between items-start mb-4">
        <div className={`p-3 rounded-xl ${iconBgClasses[color]} transition-colors duration-300`}>
          <Icon className="h-6 w-6" />
        </div>
        <button className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-gray-400 hover:text-gray-600">
          <MoreVertical className="h-4 w-4" />
        </button>
      </div>
      
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
          {title}
        </h3>
        
        <div className="flex items-end justify-between">
          <div>
            <div className="text-2xl font-bold text-gray-900">
              {loading ? (
                <div className="flex items-center space-x-2">
                  <Loader className="h-4 w-4 animate-spin" />
                  <span className="text-lg">Loading...</span>
                </div>
              ) : (
                value
              )}
            </div>
            
            {subtitle && (
              <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
            )}
          </div>
          
          {trend && (
            <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
              trend.direction === 'up' 
                ? 'bg-emerald-50 text-emerald-700' 
                : 'bg-red-50 text-red-700'
            }`}>
              {trend.direction === 'up' ? (
                <TrendingUp className="h-3 w-3" />
              ) : (
                <TrendingDown className="h-3 w-3" />
              )}
              <span>{trend.value}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MetricChart = ({ title, value, percentage, trend, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-emerald-500',
    purple: 'bg-purple-500',
    orange: 'bg-orange-500'
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex justify-between items-start mb-2">
        <span className="text-sm font-medium text-gray-600">{title}</span>
        <span className={`text-xs font-semibold ${
          trend === 'up' ? 'text-emerald-600' : 'text-red-600'
        }`}>
          {trend === 'up' ? '+' : '-'}{percentage}%
        </span>
      </div>
      <div className="text-lg font-bold text-gray-900 mb-2">{value}</div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className={`h-2 rounded-full ${colorClasses[color]} transition-all duration-1000 ease-out`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        ></div>
      </div>
    </div>
  );
};

// Utility function to format timestamps in real-time
const formatTimeAgo = (timestamp) => {
  if (!timestamp) return 'Just now';
  
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} min${diffInMinutes > 1 ? 's' : ''} ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  } else if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
};

// Hook to update timestamps in real-time
const useRealTimeTimestamps = (activities) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  return activities.map(activity => ({
    ...activity,
    formattedTime: formatTimeAgo(activity.timestamp || activity.createdAt)
  }));
};

const ActivityItem = ({ activity, index }) => {
  const getActivityConfig = (type) => {
    const configs = {
      success: {
        icon: CheckCircle2,
        iconColor: 'text-emerald-500',
        bgColor: 'bg-emerald-50',
        borderColor: 'border-emerald-200',
        textColor: 'text-emerald-700'
      },
      warning: {
        icon: AlertCircle,
        iconColor: 'text-amber-500',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-200',
        textColor: 'text-amber-700'
      },
      info: {
        icon: BookOpen,
        iconColor: 'text-indigo-500',
        bgColor: 'bg-indigo-50',
        borderColor: 'border-indigo-200',
        textColor: 'text-indigo-700'
      },
      error: {
        icon: AlertCircle,
        iconColor: 'text-rose-500',
        bgColor: 'bg-rose-50',
        borderColor: 'border-rose-200',
        textColor: 'text-rose-700'
      },
      default: {
        icon: UserCheck,
        iconColor: 'text-slate-500',
        bgColor: 'bg-slate-50',
        borderColor: 'border-slate-200',
        textColor: 'text-slate-700'
      }
    };

    return configs[type] || configs.default;
  };

  const config = getActivityConfig(activity.type);
  const IconComponent = config.icon;

  const isVeryRecent = () => {
    if (!activity.timestamp) return false;
    const activityTime = activity.timestamp instanceof Date ? activity.timestamp : new Date(activity.timestamp);
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    return activityTime > fiveMinutesAgo;
  };

  return (
    <div className={`flex items-center space-x-3 p-3 rounded-lg border-l-4 ${config.borderColor} hover:bg-slate-50 transition-all duration-200 group`}>
      <div className="flex-shrink-0">
        <div className={`p-2 rounded-lg ${config.bgColor} group-hover:bg-white transition-colors duration-200`}>
          <IconComponent className={`h-4 w-4 ${config.iconColor}`} />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900">
          {activity.description}
        </p>
        <div className="flex items-center space-x-2 text-xs text-slate-500 mt-1">
          <Clock className="h-3 w-3" />
          <span>{activity.formattedTime}</span>
          {isVeryRecent() && (
            <div className="flex items-center space-x-1 text-xs text-emerald-600 font-medium">
              <Zap className="h-3 w-3" />
              <span>Live</span>
            </div>
          )}
        </div>
      </div>
      <ArrowUpRight className="h-4 w-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
    </div>
  );
};

const RecentActivity = ({ activities, loading }) => {
  const activitiesWithRealTime = useRealTimeTimestamps(activities);

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Zap className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Live Activity</h3>
              <p className="text-sm text-slate-600">Real-time platform updates</p>
            </div>
          </div>
          <div className="relative">
            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-ping absolute -top-1 -right-1"></div>
            <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader className="h-8 w-8 animate-spin text-indigo-600 mx-auto mb-3" />
            <p className="text-slate-600">Loading activities...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Zap className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Live Activity</h3>
            <p className="text-sm text-slate-600">Real-time platform updates</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className="relative">
            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-ping absolute -top-1 -right-1"></div>
            <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
          </div>
          <button className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
            View All
          </button>
        </div>
      </div>
      
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {activitiesWithRealTime && activitiesWithRealTime.length > 0 ? (
          activitiesWithRealTime.slice(0, 6).map((activity, index) => (
            <ActivityItem key={`${activity.id || index}-${activity.timestamp}`} activity={activity} index={index} />
          ))
        ) : (
          <div className="text-center py-8">
            <div className="bg-slate-100 rounded-full p-3 w-12 h-12 mx-auto mb-3">
              <Clock className="h-6 w-6 text-slate-400 mx-auto" />
            </div>
            <p className="text-slate-500 text-sm">No recent activity</p>
            <p className="text-slate-400 text-xs mt-1">Activities will appear here in real-time</p>
          </div>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-slate-200">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>Updates every minute</span>
          <span className="flex items-center space-x-1">
            <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></div>
            <span>Live</span>
          </span>
        </div>
      </div>
    </div>
  );
};

const QuickAction = ({ icon: Icon, title, description, onClick, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200',
    green: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200',
    purple: 'bg-purple-50 text-purple-700 hover:bg-purple-100 border-purple-200'
  };

  return (
    <button
      onClick={onClick}
      className={`text-left p-4 rounded-xl border transition-all duration-300 hover:scale-105 ${colorClasses[color]}`}
    >
      <div className="flex items-center space-x-3">
        <Icon className="h-5 w-5" />
        <div>
          <div className="font-semibold text-sm">{title}</div>
          <div className="text-xs opacity-75">{description}</div>
        </div>
      </div>
    </button>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalStudents: 0,
    totalTutors: 0,
    pendingTutors: 0,
    bannedUsers: 0,
    totalAssignments: 0,
    completedAssignments: 0,
    totalRevenue: 0,
    totalCommissions: 0
  });
  
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [debugInfo, setDebugInfo] = useState({});

  // Generate sample activities with realistic timestamps
  const generateSampleActivities = () => {
    const now = new Date();
    return [
      {
        id: 1,
        type: 'success',
        description: 'New tutor application approved',
        timestamp: new Date(now.getTime() - 5 * 60 * 1000),
        createdAt: new Date(now.getTime() - 5 * 60 * 1000)
      },
      {
        id: 2,
        type: 'info',
        description: 'Assignment completed by student',
        timestamp: new Date(now.getTime() - 2 * 60 * 60 * 1000),
        createdAt: new Date(now.getTime() - 2 * 60 * 60 * 1000)
      },
      {
        id: 3,
        type: 'warning',
        description: 'Pending tutor review required',
        timestamp: new Date(now.getTime() - 30 * 60 * 1000),
        createdAt: new Date(now.getTime() - 30 * 60 * 1000)
      },
      {
        id: 4,
        type: 'success',
        description: 'Payment processed successfully',
        timestamp: new Date(now.getTime() - 45 * 60 * 1000),
        createdAt: new Date(now.getTime() - 45 * 60 * 1000)
      },
      {
        id: 5,
        type: 'info',
        description: 'New user registration',
        timestamp: new Date(now.getTime() - 3 * 60 * 1000),
        createdAt: new Date(now.getTime() - 3 * 60 * 1000)
      }
    ];
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Fetching dashboard data...');
      
      const [statsResponse, activitiesResponse] = await Promise.all([
        dashboardService.getStats(),
        dashboardService.getRecentActivities(5)
      ]);

      // Store debug info for troubleshooting
      setDebugInfo({
        statsResponse,
        activitiesResponse,
        timestamp: new Date().toISOString()
      });

      console.log('📊 Stats Response:', statsResponse);
      console.log('📝 Activities Response:', activitiesResponse);

      // Handle stats response
      if (statsResponse.success) {
        const statsData = statsResponse.data || {};
        console.log('✅ Stats data received:', statsData);
        
        setStats(prev => ({
          ...prev,
          ...statsData
        }));
      } else {
        console.warn('⚠️ Stats API failed:', statsResponse.message);
        setError(statsResponse.message || 'Failed to load statistics');
      }

      // Handle activities response
      if (activitiesResponse.success) {
        setActivities(activitiesResponse.data || generateSampleActivities());
      } else {
        setActivities(generateSampleActivities());
        console.log('📋 Using sample activities data');
      }

      setLastUpdated(new Date());
      
    } catch (err) {
      console.error('💥 Dashboard data fetch error:', err);
      setError('Failed to load dashboard data. Please try refreshing the page.');
      setActivities(generateSampleActivities());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();

    // Refresh every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const quickActions = [
    {
      icon: UserCheck,
      title: "Tutor Applications",
      description: "Review pending applications",
      onClick: () => window.location.href = '/tutor-applications',
      color: 'blue'
    },
    {
      icon: Users,
      title: "Manage Users",
      description: "View and manage all users",
      onClick: () => window.location.href = '/users',
      color: 'green'
    },
    {
      icon: BookOpen,
      title: "Assignments",
      description: "Monitor assignments",
      onClick: () => window.location.href = '/assignments',
      color: 'purple'
    }
  ];

  // Calculate derived metrics
  const studentRatio = stats.totalUsers > 0 
    ? Math.round((stats.totalStudents / stats.totalUsers) * 100) 
    : 0;
  
  const completionRate = stats.totalAssignments > 0 
    ? Math.round((stats.completedAssignments / stats.totalAssignments) * 100) 
    : 0;

  return (
    <DashboardLayout>
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard Overview</h1>
            <p className="text-gray-600 mt-2">Real-time insights and analytics for your platform</p>
            
            {lastUpdated && !loading && (
              <div className="flex items-center space-x-2 mt-1 text-sm text-gray-500">
                <Clock className="h-3 w-3" />
                <span>Last updated: {lastUpdated.toLocaleTimeString()}</span>
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-3 mt-4 sm:mt-0">
            <button 
              onClick={fetchDashboardData}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span>{loading ? 'Refreshing...' : 'Refresh'}</span>
            </button>
            
            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-300 flex items-center space-x-2">
              <BarChart3 className="h-4 w-4" />
              <span>Generate Report</span>
            </button>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && !loading && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 animate-fade-in">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
            <div>
              <p className="text-amber-800 font-medium">Partial Data Loaded</p>
              <p className="text-amber-600 text-sm">{error}</p>
              <button 
                onClick={fetchDashboardData}
                className="text-amber-700 hover:text-amber-800 text-sm font-medium mt-2 flex items-center space-x-1"
              >
                <RefreshCw className="h-3 w-3" />
                <span>Try again</span>
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Debug Info - Only show in development */}
      {process.env.NODE_ENV === 'development' && Object.keys(debugInfo).length > 0 && (
        <div className="mb-6 p-4 bg-gray-100 rounded-lg border border-gray-300">
          <details className="text-sm">
            <summary className="cursor-pointer font-medium text-gray-700">
              Debug Information (Dev Only)
            </summary>
            <div className="mt-2 p-3 bg-white rounded border">
              <pre className="text-xs overflow-auto max-h-40">
                {JSON.stringify({
                  stats,
                  loading,
                  error,
                  activitiesCount: activities.length,
                  debugInfo
                }, null, 2)}
              </pre>
            </div>
          </details>
        </div>
      )}
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatsCard
          title="Total Users"
          value={formatNumber(stats.totalUsers)}
          subtitle="Registered users"
          icon={Users}
          trend={{ direction: 'up', value: '+12%' }}
          loading={loading}
          color="primary"
        />
        
        <StatsCard
          title="Active Tutors"
          value={formatNumber(stats.totalTutors)}
          subtitle={`${formatNumber(stats.pendingTutors)} pending`}
          icon={GraduationCap}
          trend={{ direction: 'up', value: '+8%' }}
          loading={loading}
          color="green"
        />
        
        <StatsCard
          title="Assignments"
          value={formatNumber(stats.totalAssignments)}
          subtitle={`${formatNumber(stats.completedAssignments)} completed`}
          icon={FileText}
          trend={{ direction: 'up', value: '+23%' }}
          loading={loading}
          color="purple"
        />
        
        <StatsCard
          title="Platform Revenue"
          value={formatCurrency(stats.totalRevenue)}
          subtitle={`${formatCurrency(stats.totalCommissions)} commissions`}
          icon={DollarSign}
          trend={{ direction: 'up', value: '+15%' }}
          loading={loading}
          color="orange"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Metrics & Actions */}
        <div className="lg:col-span-2 space-y-6">
          {/* Metrics Overview */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Platform Performance</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <MetricChart
                title="Student Ratio"
                value={`${studentRatio}%`}
                percentage={studentRatio}
                trend="up"
                color="blue"
              />
              
              <MetricChart
                title="Completion Rate"
                value={`${completionRate}%`}
                percentage={completionRate}
                trend="up"
                color="green"
              />
            </div>

            {/* Additional Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">Banned Users</span>
                  <span className="text-lg font-bold text-red-600">{stats.bannedUsers}</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {stats.totalUsers > 0 
                    ? `${Math.round((stats.bannedUsers / stats.totalUsers) * 100)}% of total users`
                    : 'No users'
                  }
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">Avg. Assignment Value</span>
                  <span className="text-lg font-bold text-purple-600">
                    {stats.totalAssignments > 0 
                      ? formatCurrency(stats.totalRevenue / stats.totalAssignments)
                      : formatCurrency(0)
                    }
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Average per assignment
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="border-t pt-6">
              <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {quickActions.map((action, index) => (
                  <QuickAction
                    key={index}
                    icon={action.icon}
                    title={action.title}
                    description={action.description}
                    onClick={action.onClick}
                    color={action.color}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Right Column - Activity */}
        <div>
          <RecentActivity activities={activities} loading={loading} />
          
          {/* System Status Card */}
          <div className="mt-6 bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">System Status</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${!loading ? 'bg-emerald-500' : 'bg-amber-500'}`}></div>
                  <span className="text-sm text-gray-700">API Connection</span>
                </div>
                <span className={`text-sm font-medium ${!loading ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {!loading ? 'Connected' : 'Connecting...'}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                  <span className="text-sm text-gray-700">Data Freshness</span>
                </div>
                <span className="text-sm text-gray-600">
                  {lastUpdated ? `${Math.floor((new Date() - lastUpdated) / 1000)}s ago` : 'Never'}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                  <span className="text-sm text-gray-700">Activities</span>
                </div>
                <span className="text-sm text-gray-600">{activities.length} loaded</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;